import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerLog } from '../../services/customerLog.service';
import { Button, EmptyState, LogEntry } from '../../components/core';
import { tsToDate } from '../../utils/timestamp';

const PAGE_SIZE = 20;

interface LogListProps {
  logs: CustomerLog[];
  currentUserId?: string;
  /** True when the current user has customers.delete permission (admin). Allows deleting any entry. */
  canDeleteAny?: boolean;
  /** Called when the author saves an edit. Returns the updated log id + new message. */
  onEdit?: (_logId: string, _newMessage: string) => Promise<void>;
  /** Called when the author (or admin) deletes a log entry. */
  onDelete?: (_logId: string) => Promise<void>;
  /** Called when any user taps "Set Reminder" on a log entry. Receives the log message. */
  onRemind?: (_logMessage: string) => void;
}

export function LogList({ logs, currentUserId, canDeleteAny, onEdit, onDelete, onRemind }: LogListProps) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const visibleLogs = logs.slice(0, visibleCount);
  const hasMore = visibleCount < logs.length;

  if (logs.length === 0) {
    return (
      <EmptyState
        emoji="📝"
        title={t('customers.noLogs')}
        description={t('customers.noLogsHelp')}
      />
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded border border-green-200 bg-white">
        {visibleLogs.map((log) => {
          const isAuthor = !!currentUserId && currentUserId === log.createdBy;
          const canDelete = isAuthor || !!canDeleteAny;
          return (
            <LogEntry
              key={log.id}
              message={log.message}
              authorInitials={(log.userDisplayName ?? '?')[0].toUpperCase()}
              authorName={log.userDisplayName ?? ''}
              timestamp={tsToDate(log.createdAt) ?? new Date()}
              isEdited={!!log.updatedAt}
              onSave={
                isAuthor && onEdit
                  ? (newMsg) => onEdit(log.id, newMsg)
                  : undefined
              }
              onDelete={
                canDelete && onDelete ? () => onDelete(log.id) : undefined
              }
              onRemind={onRemind ? () => onRemind(log.message) : undefined}
            />
          );
        })}
      </div>

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            {t('customers.loadMore')}
          </Button>
        </div>
      )}
    </>
  );
}
